import React, { useState } from 'react';
import { Brain, ThumbsUp, ThumbsDown, Meh } from 'lucide-react';

export const UnderstandingRating = ({ blockIndex, blockContent, onRate, disabled }) => {
  const [understanding, setUnderstanding] = useState(null);
  const [confidence, setConfidence] = useState(3);
  const [needsReview, setNeedsReview] = useState(false);
  const [submitted, setSubmitted] = useState(disabled);

  const handleSubmit = () => {
    if (understanding === null) {
      alert('Please rate your understanding');
      return;
    }

    const data = {
      blockIndex,
      understanding,
      confidence,
      needsReview,
      submittedAt: new Date().toISOString()
    };

    onRate(data);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="text-center py-4">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Brain className="w-6 h-6 text-green-600" />
        </div>
        <p className="text-sm text-gray-600">Thanks for your feedback!</p>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Brain className="w-6 h-6 text-blue-600" />
        <h3 className="text-lg font-bold text-gray-900">How well did you understand this?</h3>
      </div>

      {/* Understanding Level */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Understanding Level
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setUnderstanding('low')}
            className={`flex flex-col items-center justify-center gap-2 p-4 border-2 rounded-lg transition-all ${
              understanding === 'low'
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300 hover:border-red-400'
            }`}
          >
            <ThumbsDown className={`w-6 h-6 ${understanding === 'low' ? 'text-red-600' : 'text-gray-400'}`} />
            <span className={`text-sm font-medium ${understanding === 'low' ? 'text-red-700' : 'text-gray-600'}`}>
              Confused
            </span>
          </button>

          <button
            onClick={() => setUnderstanding('medium')}
            className={`flex flex-col items-center justify-center gap-2 p-4 border-2 rounded-lg transition-all ${
              understanding === 'medium'
                ? 'border-yellow-500 bg-yellow-50'
                : 'border-gray-300 hover:border-yellow-400'
            }`}
          >
            <Meh className={`w-6 h-6 ${understanding === 'medium' ? 'text-yellow-600' : 'text-gray-400'}`} />
            <span className={`text-sm font-medium ${understanding === 'medium' ? 'text-yellow-700' : 'text-gray-600'}`}>
              Somewhat
            </span>
          </button>

          <button
            onClick={() => setUnderstanding('high')}
            className={`flex flex-col items-center justify-center gap-2 p-4 border-2 rounded-lg transition-all ${
              understanding === 'high'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 hover:border-green-400'
            }`}
          >
            <ThumbsUp className={`w-6 h-6 ${understanding === 'high' ? 'text-green-600' : 'text-gray-400'}`} />
            <span className={`text-sm font-medium ${understanding === 'high' ? 'text-green-700' : 'text-gray-600'}`}>
              Clear
            </span>
          </button>
        </div>
      </div>

      {/* Confidence Slider */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Confidence Level: {confidence}/5
        </label>
        <input
          type="range"
          min="1"
          max="5"
          value={confidence}
          onChange={(e) => setConfidence(parseInt(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>Not Confident</span>
          <span>Very Confident</span>
        </div>
      </div>

      {/* Needs Review */}
      <div className="mb-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={needsReview}
            onChange={(e) => setNeedsReview(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">I need to review this section</span>
        </label>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700"
      >
        Submit Feedback
      </button>
    </div>
  );
};